import { CSSProperties, MouseEvent, ReactNode, useMemo } from 'react';
import { useToggle } from 'react-use';
import { MultiSelectActionBar } from 'src/components/common/MultiSelect/MultiSelectActionBar';
import { PerformerVenueList } from 'src/components/Events/PerformerVenueList';
import { ColumnResizingContextProvider } from 'src/contexts/ColumnResizingContext/ColumnResizingContext';
import { Content } from 'src/contexts/ContentContext';
import { useEventDiscoveryContext } from 'src/contexts/EventDiscoveryContext/EventDiscoveryContext';
import { ExplorerLayout } from 'src/core/layouts';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ColumnSettingsModal } from 'src/modals/ColumnSettings/ColumnSettings';
import { LayoutIcon } from 'src/svgs';
import { CustomOnSaleEventsColumn } from 'src/utils/columns/events/eventsCustomColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { SectionType } from 'src/utils/types/sectionType';
import {
  ActionOutboxEntityType,
  Feature,
  UserSetting,
} from 'src/WebApiController';

import { OnSaleEventCatchUpAction } from './OnSaleDiscoveryCatchUpAction';
import { OnSaleDiscoveryTable } from './OnSaleDiscoveryTable';
import * as styles from './OnSaleDiscoveryView.css';
import { OnSaleEventTableColumnId } from './OnSaleDiscoveryView.types';

export const OnSaleDiscoveryView = () => {
  const [isColumnModalOpen, toggleColumnModal] = useToggle(false);
  const onColumnSettingButtonClickHandler = (
    e: MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    if (!isColumnModalOpen) toggleColumnModal(true);
  };

  return (
    <ColumnResizingContextProvider
      userSettingId={UserSetting.OnSaleEventColumnsWidths}
    >
      {isColumnModalOpen ? (
        <ColumnSettingsModal<CustomOnSaleEventsColumn>
          onClose={toggleColumnModal}
          sectionType={SectionType.OnSaleEvents}
        />
      ) : null}

      <OnSaleEventTableWithInnerGrouping
        columnSettingButton={
          <Button
            variant="textPlain"
            onClick={onColumnSettingButtonClickHandler}
          >
            <LayoutIcon size={vars.iconSize.m} />
            <Content id={ContentId.Columns} />
          </Button>
        }
      />
    </ColumnResizingContextProvider>
  );
};

const posTableHeadStyle: CSSProperties = {
  position: 'sticky',
  top: '0',
};

interface EventsTableWithInnerGroupingProps {
  columnSettingButton?: ReactNode;
}

const OnSaleEventTableWithInnerGrouping = ({
  columnSettingButton,
}: EventsTableWithInnerGroupingProps) => {
  const {
    isLoading,
    onSaleEvents,
    performers,
    venues,
    onEventsViewed,
    performsVenuesLoaded,
  } = useEventDiscoveryContext();

  const hasHideEventDiscoveryDemandDataFeature = useUserHasFeature(
    Feature.HideEventDiscoveryDemandData
  );

  const initialState = useMemo(() => {
    if (hasHideEventDiscoveryDemandDataFeature) {
      return {
        sorting: [
          {
            id: OnSaleEventTableColumnId.RecentVisits,
            desc: true,
          },
        ],
        grouping: [OnSaleEventTableColumnId.RecentVisits],
      };
    }
    return {
      sorting: [
        {
          id: OnSaleEventTableColumnId.VisitsLastDay,
          desc: true,
        },
      ],
      grouping: [OnSaleEventTableColumnId.VisitsLastDay],
    };
  }, [hasHideEventDiscoveryDemandDataFeature]);

  return (
    <ExplorerLayout
      showExpandButton
      leftPanelContent={
        <PerformerVenueList
          entityType={ActionOutboxEntityType.Listing}
          performerInventory={performers}
          venueInventory={venues}
          isInventoryReady={performsVenuesLoaded}
          hideMetrics
        />
      }
    >
      <Stack direction="column" height="full" width="full">
        <MultiSelectActionBar>
          <OnSaleEventCatchUpAction />
        </MultiSelectActionBar>
        <Stack
          justifyContent="end"
          alignItems="center"
          className={styles.header}
          gap="m"
        >
          {columnSettingButton}
        </Stack>
        <OnSaleDiscoveryTable
          events={onSaleEvents}
          isLoading={isLoading}
          failedToRetrieveData={false}
          tableHeadStyle={posTableHeadStyle}
          initState={initialState}
          onEventsRendered={onEventsViewed}
        />
      </Stack>
    </ExplorerLayout>
  );
};
